import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  Operation, 
  OperationType, 
  Machine, 
  Part, 
  ToolFixture, 
  WorkInstruction, 
  OperationInput, 
  OperationStatistics 
} from '../../services/operationsApi';

// State interface
export interface OperationsState {
  // Operations management
  operations: {
    items: Operation[];
    selectedOperation: Operation | null;
    loading: boolean;
    error: string | null;
    validation: Record<string, string>;
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
    filters: {
      search: string;
      active?: boolean;
      operationTypeId?: string;
      machineId?: string;
      partId?: string;
    };
    statistics: OperationStatistics | null;
    bulkSelection: string[];
  };

  // Dropdown/related data
  dropdowns: {
    operationTypes: OperationType[];
    machines: Machine[];
    parts: Part[];
    toolFixtures: ToolFixture[];
    workInstructions: WorkInstruction[];
    inputs: OperationInput[];
    loading: boolean;
    error: string | null;
  };

  // UI states
  ui: {
    operationDialogOpen: boolean;
    detailDialogOpen: boolean;
    detailDialogType: 'inputs' | 'workInstructions' | 'toolFixtures' | null;
    detailDialogData: any[];
    detailDialogTitle: string;
    bulkActionsDialogOpen: boolean;
  };
}

const initialState: OperationsState = {
  operations: {
    items: [],
    selectedOperation: null,
    loading: false,
    error: null,
    validation: {},
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      search: '',
      active: undefined,
      operationTypeId: undefined,
      machineId: undefined,
      partId: undefined,
    },
    statistics: null,
    bulkSelection: [],
  },
  dropdowns: {
    operationTypes: [],
    machines: [],
    parts: [],
    toolFixtures: [],
    workInstructions: [],
    inputs: [],
    loading: false,
    error: null,
  },
  ui: {
    operationDialogOpen: false,
    detailDialogOpen: false,
    detailDialogType: null,
    detailDialogData: [],
    detailDialogTitle: '',
    bulkActionsDialogOpen: false,
  },
};

const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    // Operations actions
    fetchOperationsStart: (state) => {
      state.operations.loading = true;
      state.operations.error = null;
    },
    fetchOperationsSuccess: (state, action: PayloadAction<{ operations: Operation[]; total: number; page: number }>) => {
      state.operations.loading = false;
      state.operations.items = action.payload.operations;
      state.operations.pagination.total = action.payload.total;
      state.operations.pagination.page = action.payload.page;
    },
    fetchOperationsFailure: (state, action: PayloadAction<string>) => {
      state.operations.loading = false;
      state.operations.error = action.payload;
    },

    setSelectedOperation: (state, action: PayloadAction<Operation | null>) => {
      state.operations.selectedOperation = action.payload;
      state.operations.validation = {};
    },

    setOperationValidation: (state, action: PayloadAction<Record<string, string>>) => {
      state.operations.validation = action.payload;
    },

    setOperationFilters: (state, action: PayloadAction<Partial<OperationsState['operations']['filters']>>) => {
      state.operations.filters = { ...state.operations.filters, ...action.payload };
      state.operations.pagination.page = 1; // Reset to first page when filters change
    },

    setOperationPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.operations.pagination.page = action.payload.page;
      state.operations.pagination.limit = action.payload.limit;
    },

    setOperationBulkSelection: (state, action: PayloadAction<string[]>) => {
      state.operations.bulkSelection = action.payload;
    },

    setOperationStatistics: (state, action: PayloadAction<OperationStatistics>) => {
      state.operations.statistics = action.payload;
    },

    // Dropdown actions
    fetchDropdownsStart: (state) => {
      state.dropdowns.loading = true;
      state.dropdowns.error = null;
    },
    fetchDropdownsSuccess: (state, action: PayloadAction<{
      operationTypes: OperationType[];
      machines: Machine[];
      parts: Part[];
      toolFixtures: ToolFixture[];
      workInstructions: WorkInstruction[];
      inputs: OperationInput[];
    }>) => {
      state.dropdowns.loading = false;
      state.dropdowns.operationTypes = action.payload.operationTypes;
      state.dropdowns.machines = action.payload.machines;
      state.dropdowns.parts = action.payload.parts;
      state.dropdowns.toolFixtures = action.payload.toolFixtures;
      state.dropdowns.workInstructions = action.payload.workInstructions;
      state.dropdowns.inputs = action.payload.inputs;
    },
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdowns.loading = false;
      state.dropdowns.error = action.payload;
    },

    // UI actions
    setOperationDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.operationDialogOpen = action.payload;
      if (!action.payload) {
        state.operations.selectedOperation = null;
        state.operations.validation = {};
      }
    },

    setDetailDialogOpen: (state, action: PayloadAction<{
      open: boolean;
      type?: 'inputs' | 'workInstructions' | 'toolFixtures';
      data?: any[];
      title?: string;
    }>) => {
      state.ui.detailDialogOpen = action.payload.open;
      if (action.payload.open) {
        state.ui.detailDialogType = action.payload.type || null;
        state.ui.detailDialogData = action.payload.data || [];
        state.ui.detailDialogTitle = action.payload.title || '';
      } else {
        state.ui.detailDialogType = null;
        state.ui.detailDialogData = [];
        state.ui.detailDialogTitle = '';
      }
    },

    setBulkActionsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.bulkActionsDialogOpen = action.payload;
    },

    // Clear errors
    clearOperationError: (state) => {
      state.operations.error = null;
    },
    clearDropdownError: (state) => {
      state.dropdowns.error = null;
    },

    // Reset state
    resetOperationState: (state) => {
      state.operations = initialState.operations;
    },
    resetOperationsState: () => initialState,
  },
});

export const {
  // Operations actions
  fetchOperationsStart,
  fetchOperationsSuccess,
  fetchOperationsFailure,
  setSelectedOperation,
  setOperationValidation,
  setOperationFilters,
  setOperationPagination,
  setOperationBulkSelection,
  setOperationStatistics,

  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  // UI actions
  setOperationDialogOpen,
  setDetailDialogOpen,
  setBulkActionsDialogOpen,

  // Clear errors
  clearOperationError,
  clearDropdownError,

  // Reset state
  resetOperationState,
  resetOperationsState,
} = operationsSlice.actions;

export const operationsReducer = operationsSlice.reducer; 